import { Link } from "react-router-dom";
import { LegalPage, Sec, type TocEntry } from "../components/LegalPage";
import { COMPANY, PLATFORM_LINKS } from "../lib/company";
import { EmailLink } from "../components/EmailLink";

const TOC: TocEntry[] = [
  { id: "intro", title: "Introduction" },
  { id: "definitions", title: "Definitions" },
  { id: "nature", title: "Nature of the Service" },
  { id: "eligibility", title: "Eligibility" },
  { id: "accounts", title: "Accounts and Workspaces" },
  { id: "connecting", title: "Connecting Social Accounts" },
  { id: "platforms", title: "Third-Party Platforms" },
  { id: "publishing", title: "Scheduling and Publishing" },
  { id: "content", title: "Your Content and Licence" },
  { id: "conduct", title: "Acceptable Use" },
  { id: "restricted", title: "Prohibited and Restricted Content" },
  { id: "security", title: "Security, Hacking and Cyber Attacks" },
  { id: "moderation", title: "Reporting, Review and Removal" },
  { id: "enforcement", title: "Enforcement, Suspension and Refunds" },
  { id: "compliance", title: "Platform Policy Compliance" },
  { id: "fees", title: "Fees and Payment" },
  { id: "availability", title: "Availability and Changes" },
  { id: "ip", title: "Intellectual Property" },
  { id: "privacy", title: "Privacy" },
  { id: "termination", title: "Ending Your Account" },
  { id: "disclaimer", title: "Disclaimers" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "indemnity", title: "Indemnification" },
  { id: "law", title: "Governing Law" },
  { id: "changes", title: "Changes to Terms" },
  { id: "contact", title: "Contact and Grievance Officer" },
];

export function Terms() {
  return (
    <div className="wrap wrap-legal">
      <LegalPage
        title="Terms of Service"
        toc={TOC}
        intro={<>
          These Terms govern your access to and use of {COMPANY.product}, including our website,
          mobile application, scheduling tools, media library, publishing pipeline and related
          services.
        </>}
      >
        <Sec id="intro" n={1} title="Introduction">
          <p>
            By accessing or using {COMPANY.product}, you agree to these Terms of Service. If you are
            accepting on behalf of an organisation, you confirm you have authority to bind that
            organisation. If you do not agree, do not use the platform.
          </p>
          <p>
            Sections <a href="#restricted">11</a>, <a href="#security">12</a> and{" "}
            <a href="#enforcement">14</a> describe content and conduct that are not permitted here
            and what we will do about them. Read them before you upload anything.
          </p>
          <p>
            Related documents: <Link to="/privacy">Privacy Policy</Link>,{" "}
            <Link to="/data-deletion">Delete your data</Link> and <Link to="/security">Security</Link>.
          </p>
        </Sec>

        <Sec id="definitions" n={2} title="Definitions">
          <p><strong>{COMPANY.product}</strong>, <strong>Company</strong>, <strong>we</strong> or{" "}
            <strong>us</strong> means {COMPANY.legalName}, which operates the {COMPANY.product} platform.</p>
          <p><strong>Platform</strong> means the website, mobile application, APIs, scheduling
            service, storage, publishing pipeline and related tools.</p>
          <p><strong>User</strong> or <strong>you</strong> means any person or organisation that
            accesses or uses the Platform.</p>
          <p><strong>Workspace</strong> means the container that holds your connected accounts,
            media and posts, and to which other members may be invited.</p>
          <p><strong>Connected Account</strong> means a Facebook Page, Instagram Business or Creator
            account, YouTube channel, or other third-party account you authorise us to publish to.</p>
          <p><strong>Network</strong> means Meta Platforms, Inc., Google LLC or another operator of a
            Connected Account.</p>
          <p><strong>Your Content</strong> means captions, titles, descriptions, photos, videos and
            other material you upload, import, store or create on the Platform.</p>
          <p><strong>Restricted Content</strong> means anything listed in{" "}
            <a href="#restricted">Section 11</a>.</p>
          <p><strong>Security Incident</strong> means any act described in{" "}
            <a href="#security">Section 12</a>.</p>
        </Sec>

        <Sec id="nature" n={3} title="Nature of the Service">
          <p>
            {COMPANY.product} is a publishing tool. You compose a post once, attach media, choose
            which Connected Accounts should receive it, and publish it immediately or at a time you
            schedule. At that time we call each Network&rsquo;s official API on your behalf, using
            the authorisation you granted.
          </p>
          <p>
            We are a neutral technical intermediary. We provide storage and transmission; we do not
            create, select, commission, endorse or edit Your Content, and we do not choose who
            receives it. You are the publisher of everything you send through the Platform, and you
            alone are responsible for it.
          </p>
          <p>
            We are an independent product. We are not affiliated with, endorsed by, sponsored by or
            operated in partnership with Meta Platforms, Inc. or Google LLC. We do not control the
            Networks, their APIs, their review processes, their rate limits or their moderation
            decisions.
          </p>
        </Sec>

        <Sec id="eligibility" n={4} title="Eligibility">
          <p>
            You must be at least 18 years old and legally capable of entering a contract. You must
            have the right to publish to every Connected Account you add, whether as its owner or as
            an authorised administrator acting with the owner&rsquo;s permission.
          </p>
        </Sec>

        <Sec id="accounts" n={5} title="Accounts and Workspaces">
          <p>
            Sign-in uses a one-time link sent to your email address. You are responsible for keeping
            that mailbox secure, since access to it is access to your account. Notify us promptly at{" "}
            <EmailLink address={COMPANY.supportEmail} /> if you suspect
            unauthorised access.
          </p>
          <p>
            Members of a Workspace can see and act on that Workspace&rsquo;s posts, media and
            Connected Accounts. Invite only people you trust with that access. You are responsible
            for activity carried out through your account and your Workspace, including uploads made
            by anyone you invited, whether or not you authorised the particular upload.
          </p>
          <p>You agree to provide accurate information and to keep it current.</p>
        </Sec>

        <Sec id="connecting" n={6} title="Connecting Social Accounts">
          <p>
            Connecting an account uses each Network&rsquo;s official authorisation flow. You enter
            your credentials on the Network&rsquo;s own website, never on ours. We receive a
            revocable access token limited to the permissions you approve, and we never receive your
            password.
          </p>
          <p>
            You may disconnect an account inside {COMPANY.product} at any time, or revoke our access
            directly at the Network. Doing so takes effect immediately and will cause any pending
            posts to that account to fail rather than publish.
          </p>
          <p>
            Networks expire, invalidate or downgrade tokens for their own reasons, including password
            changes, permission changes, security events and policy enforcement. When that happens
            you will need to reconnect the account.
          </p>
        </Sec>

        <Sec id="platforms" n={7} title="Third-Party Platforms">
          <p>
            Your use of each Connected Account remains governed by that Network&rsquo;s own terms and
            policies, including the{" "}
            <a href={PLATFORM_LINKS.metaTerms} target="_blank" rel="noreferrer noopener">Meta Terms of Service</a>,{" "}
            <a href={PLATFORM_LINKS.instagramTerms} target="_blank" rel="noreferrer noopener">Instagram Terms of Use</a>{" "}
            and the{" "}
            <a href={PLATFORM_LINKS.youtubeTerms} target="_blank" rel="noreferrer noopener">YouTube Terms of Service</a>.
            By connecting a YouTube channel you agree to be bound by the YouTube Terms of Service, and
            Google&rsquo;s{" "}
            <a href={PLATFORM_LINKS.googlePrivacy} target="_blank" rel="noreferrer noopener">Privacy Policy</a>{" "}
            applies to data Google receives.
          </p>
          <p>
            Nothing published through {COMPANY.product} is exempt from those rules. A Network may
            reject, remove, restrict or age-gate your content, or suspend your account, on its own
            terms and without notice to us.
          </p>
          <p>
            Each Network&rsquo;s community standards apply to your content in addition to these
            Terms. Content permitted here may still be removed there, and content a Network tolerates
            may still breach <a href="#restricted">Section 11</a>. Where the two differ, the stricter
            rule governs what you may publish through the Platform.
          </p>
        </Sec>

        <Sec id="publishing" n={8} title="Scheduling and Publishing">
          <p>
            Scheduling is a request, not a guarantee. Publication depends on the Network&rsquo;s API
            being available, your token being valid, your content meeting the Network&rsquo;s
            technical and policy requirements, and applicable rate limits not being exhausted.
          </p>
          <p>
            Networks enforce publishing limits — for example a daily cap on posts per account — that
            we cannot raise. Where a delivery fails for a reason that may be temporary we retry it a
            limited number of times with increasing delays. Where it fails permanently we stop and
            report the reason.
          </p>
          <p>
            Each target account is delivered independently. A post may succeed on one Network and
            fail on another, and we will show you exactly which.
          </p>
          <p>
            We check your media against each Network&rsquo;s published limits before publishing and
            warn you when a file will be rejected. These checks are technical only — they measure
            file type, size, dimensions and duration. They are not a review of what the media
            depicts, and a file that passes them has not been approved by us in any sense.
          </p>
          <p>
            You are responsible for what you schedule, when it goes out, and whether it is
            appropriate at the moment it publishes.
          </p>
        </Sec>

        <Sec id="content" n={9} title="Your Content and Licence">
          <p>
            You retain all ownership of Your Content. We claim no ownership over anything you upload
            or publish.
          </p>
          <p>
            You grant us a limited, worldwide, non-exclusive, royalty-free licence to host, store,
            copy, transmit, reformat and transmit Your Content solely to operate the Platform and to
            deliver it to the Connected Accounts you select. This licence exists only to run the
            service and ends when you delete the content or your account, subject to the retention
            described in the <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            You confirm that you own or have the necessary rights, licences and permissions for Your
            Content, including any music, footage, images, trademarks or likenesses it contains, and
            that publishing it does not infringe the rights of anyone else or breach any law.
          </p>
          <p>
            You further confirm, each time you upload or publish, that Your Content is not Restricted
            Content and does not breach <a href="#restricted">Section 11</a>. This confirmation is a
            condition of the licence above; it is withdrawn automatically for any content found to be
            in breach.
          </p>
          <p>
            We do not use Your Content to train machine-learning models and we do not sell it.
          </p>
        </Sec>

        <Sec id="conduct" n={10} title="Acceptable Use">
          <p>You must not use {COMPANY.product} to:</p>
          <ul>
            <li>publish content that is unlawful, defamatory, obscene, hateful, harassing, deceptive
              or that infringes intellectual property or privacy rights;</li>
            <li>publish to accounts you do not own or are not authorised to administer;</li>
            <li>operate spam networks, engagement farms, coordinated inauthentic behaviour or bulk
              duplicate posting across unrelated accounts;</li>
            <li>circumvent, mask or artificially inflate any Network&rsquo;s rate limits, review
              processes or enforcement;</li>
            <li>resell, sublicense or white-label the Platform without our written agreement;</li>
            <li>use the Platform, or the media stored in it, as general-purpose file storage,
              a file-sharing or distribution service, or a content delivery network unrelated to
              publishing;</li>
            <li>reverse engineer, scrape, probe, overload or interfere with the Platform or its
              security;</li>
            <li>upload malware, or attempt to access another Workspace&rsquo;s data.</li>
          </ul>
        </Sec>

        <Sec id="restricted" n={11} title="Prohibited and Restricted Content">
          <p className="warn">
            <strong>
              Do not upload, store, import, schedule or publish any of the following through{" "}
              {COMPANY.product}. This applies to media kept privately in your library as much as to
              anything you send to a Network — storing it here is itself a breach, whether or not it
              is ever published.
            </strong>
          </p>
          <ul>
            <li><strong>Violence and graphic material</strong> — real or simulated violence
              presented gratuitously, gore, mutilation, torture, animal cruelty, footage of
              atrocities, accidents or deaths shared for shock value, and threats of violence
              against any person or group.</li>
            <li><strong>Sexual content and pornography</strong> — pornography, sexually explicit
              imagery or video, nudity intended to be sexually gratifying, sexual services,
              prostitution or escort advertising, and non-consensual intimate imagery, including
              images altered or synthesised to appear intimate.</li>
            <li><strong>Child sexual abuse material and child endangerment</strong> — any sexualised
              depiction of a minor, real, drawn or synthetic, and any content that grooms,
              solicits, endangers or exploits a minor. This category is reported to the authorities
              and results in permanent termination without warning; nothing elsewhere in these
              Terms softens that.</li>
            <li><strong>Hate speech</strong> — content attacking, dehumanising, demeaning or
              inciting hatred, violence or exclusion against people on the basis of religion, caste,
              race, ethnicity, national origin, sex, gender, gender identity, sexual orientation,
              disability, age or serious disease, and the promotion of hateful ideologies or their
              symbols.</li>
            <li><strong>Terrorism, extremism and organised crime</strong> — content that praises,
              supports, represents or recruits for terrorist organisations, violent extremist
              movements or criminal organisations, and instructional material for carrying out
              attacks.</li>
            <li><strong>Weapons, firearms and explosives</strong> — the sale, brokering, transfer or
              private trade of firearms, firearm parts, ammunition, magazines, 3D-printed or
              untraceable weapons, explosives or their precursors, and instructions for
              manufacturing, converting or modifying any of them.</li>
            <li><strong>Drugs and regulated goods</strong> — the sale or facilitation of illegal
              drugs, controlled substances, prescription medicines without authorisation,
              endangered wildlife or their parts, human organs, counterfeit goods, stolen goods and
              forged documents.</li>
            <li><strong>Suicide, self-harm and eating disorders</strong> — content that promotes,
              instructs, coordinates or encourages suicide, self-injury or disordered eating.</li>
            <li><strong>Harassment, bullying and doxxing</strong> — targeted abuse of an individual,
              coordinated pile-ons, and publishing another person&rsquo;s private information —
              address, identity numbers, financial or medical records, private communications —
              without their consent.</li>
            <li><strong>Impersonation and deception</strong> — impersonating a person, business,
              brand or public authority, synthetic or manipulated media presented as a real
              recording of a real person, and content designed to deceive about who is speaking.</li>
            <li><strong>Fraud, scams and financial harm</strong> — phishing, advance-fee and
              investment fraud, Ponzi and pyramid schemes, fake giveaways, credential or payment-card
              trading, and money laundering.</li>
            <li><strong>Malicious and unlawfully obtained material</strong> — malware, viruses,
              exploit code, hacking tools distributed for attack, and data obtained through a breach
              or without authorisation.</li>
            <li><strong>Infringing content</strong> — material that infringes copyright, trademark,
              design, patent, publicity or privacy rights, including music, film, broadcast footage
              and photography you do not have the rights to publish.</li>
            <li><strong>Anything unlawful in India or in your own jurisdiction</strong> — including
              content prohibited by the Information Technology Act, 2000 and rules made under it,
              and content a court or competent authority has ordered removed.</li>
          </ul>
          <p>
            This list describes categories, not an exhaustive catalogue. Content that plainly belongs
            to a listed category is Restricted Content even if its exact form is not named, and we
            may treat material as Restricted Content where a reasonable person would regard it as
            falling within one of these categories. Our assessment, made in good faith, governs for
            the purposes of these Terms.
          </p>
          <p>
            Narrow exceptions may be made, at our sole discretion and only on request in advance, for
            genuine news reporting, documentary, scientific, medical, educational or artistic
            purposes where the material is clearly contextualised and is not gratuitous. No exception
            is available for child sexual abuse material or non-consensual intimate imagery.
          </p>
          <p>
            You must also comply with each Network&rsquo;s own community standards. Breaching them
            through the Platform is a breach of these Terms as well, and exposes us to enforcement by
            that Network, which we are entitled to pass through to you.
          </p>
        </Sec>

        <Sec id="security" n={12} title="Security, Hacking and Cyber Attacks">
          <p>
            The Platform, its infrastructure, its databases, its Edge Functions, its APIs and the
            accounts and data held in it are protected computer resources. You must not, and must not
            permit or assist anyone else to:
          </p>
          <ul>
            <li>access or attempt to access any account, Workspace, database, storage bucket, log,
              token or system that you are not authorised to access, whether by exploiting a
              vulnerability, by guessing or stuffing credentials, by session hijacking, or by any
              other means;</li>
            <li>scan, probe, fuzz, penetration-test, vulnerability-test or otherwise test the
              security of the Platform without our prior written authorisation — an authorisation we
              grant in a signed scope document and in no other way. Publicly available bug-bounty
              language, an offer to &ldquo;test responsibly&rdquo;, or a claim of research intent is
              not authorisation;</li>
            <li>conduct or contribute to a denial-of-service or distributed denial-of-service attack,
              a traffic flood, an amplification attack, or any activity that degrades, destabilises
              or imposes a disproportionate load on the Platform or on any Network we call on your
              behalf;</li>
            <li>introduce, host, transmit or link to malware, viruses, worms, trojans, ransomware,
              keyloggers, cryptominers, rootkits, backdoors or any other harmful, destructive or
              self-propagating code;</li>
            <li>scrape, harvest, crawl or bulk-extract data from the Platform, whether by script,
              bot, headless browser or automated agent, except through interfaces we publish for
              that purpose and within their documented limits;</li>
            <li>reverse engineer, decompile, disassemble, tamper with, circumvent or disable any
              authentication, authorisation, encryption, rate limit, quota, audit log, watermark or
              other technical protection measure;</li>
            <li>intercept, alter or replay traffic between the Platform and any Network, or forge,
              reuse, extract or resell access tokens, refresh tokens, API keys or signed URLs;</li>
            <li>use the Platform as infrastructure for an attack on anyone else — as a proxy, a
              staging host, a command-and-control channel, a phishing origin or a mail relay.</li>
          </ul>
          <p>
            Any of these is a <strong>material breach</strong> and, we expect, an offence. In India
            it may constitute an offence under sections 43, 65, 66, 66C, 66F and 43A of the
            Information Technology Act, 2000 and under the Bharatiya Nyaya Sanhita, 2023, and may be
            an offence under comparable law where you are located. Nothing in these Terms grants you
            authorisation to access any computer resource, and no part of the Platform being reachable
            over the internet should be read as consent to test it.
          </p>
          <p>
            Where we reasonably suspect a Security Incident we may, immediately and without prior
            notice, suspend or terminate the account and every Workspace it can reach, revoke tokens,
            block network addresses, freeze content and preserve logs, session records, IP addresses
            and uploaded files as evidence for as long as any investigation or proceeding requires —
            including beyond the retention periods stated in the{" "}
            <Link to="/privacy">Privacy Policy</Link>, where the law permits retention for that
            purpose. We may report the matter to law enforcement, to CERT-In, to the affected
            Networks and to any affected user, and we may disclose your account, billing and
            connection records to them.
          </p>
          <p>
            You are liable to us for all loss, damage, downtime, forensic and remediation cost,
            third-party claims, regulatory penalties and legal fees arising from a Security Incident
            attributable to you, your credentials or your Workspace, and{" "}
            <a href="#indemnity">Section 23</a> applies to it in full. The limitation of liability in{" "}
            <a href="#liability">Section 22</a> caps what <em>we</em> owe <em>you</em>; it does not
            cap what you owe us.
          </p>
          <p>
            If you discover a vulnerability, report it to <EmailLink address={COMPANY.supportEmail} />{" "}
            with enough detail to reproduce it, and do not exploit it, pivot with it, access anyone
            else&rsquo;s data with it, or disclose it publicly before we have had a reasonable
            opportunity to fix it. Reports made and handled that way are welcome and we will not
            pursue you for them.
          </p>
        </Sec>

        <Sec id="moderation" n={13} title="Reporting, Review and Removal">
          <p>
            We do not pre-screen, monitor or review Your Content before it is stored or published,
            and we have no general obligation to monitor what passes through the Platform. Automated
            checks on file type, size and duration are technical validation, not moderation.
          </p>
          <p>
            We reserve the right, without any duty to exercise it, to review, investigate, restrict,
            disable, remove or refuse to transmit any content — at any time, with or without notice —
            where we reasonably believe it is Restricted Content, breaches these Terms or the law,
            breaches a Network&rsquo;s policies, or exposes us, our users, our providers or a Network
            to legal or security risk. Exercising that right does not make us the author, publisher
            or editor of anything, does not create an obligation to review anything else, and does
            not waive any other right.
          </p>
          <p>
            To report content, conduct or a suspected security issue, write to{" "}
            <EmailLink address={COMPANY.supportEmail} />. Tell us what the content is, where it is,
            why it breaches these Terms or the law, and how to reach you. Where you are complaining
            on someone&rsquo;s behalf, say so and confirm you are authorised.
          </p>
          <p>
            We act on valid complaints as quickly as we reasonably can. We remove or disable content
            within 36 hours of receiving an order of a court of competent jurisdiction or a
            notification from an appropriate government agency, and within 24 hours of a complaint
            about content that exposes a person&rsquo;s private areas, shows them in full or partial
            nudity or in a sexual act, or is impersonation including a morphed or synthetic image.
            These timelines reflect Rules 3(1)(d) and 3(2)(b) of the Information Technology
            (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.
          </p>
          <p>
            Removing content from the Platform does not remove anything already published to a
            Network. That has to be deleted at the Network itself, and we may not be able to do it
            for you.
          </p>
        </Sec>

        <Sec id="enforcement" n={14} title="Enforcement, Suspension and Refunds">
          <p>
            Where we reasonably believe you have breached these Terms — in particular{" "}
            <a href="#conduct">Section 10</a>, <a href="#restricted">Section 11</a> or{" "}
            <a href="#security">Section 12</a> — we may take one or more of the following measures,
            choosing what is proportionate to the seriousness, the harm caused, whether it was
            deliberate, and your history:
          </p>
          <ul>
            <li>a warning, and a requirement that you remove the content or correct the problem;</li>
            <li>removal or disabling of the content, and revocation of a Connected Account&rsquo;s
              authorisation;</li>
            <li>restriction of features — for example blocking uploads, imports, scheduling or
              publishing while the rest of the account keeps working;</li>
            <li><strong>temporary suspension of the account and its Workspaces for a period we
              determine, up to a maximum of one month</strong>;</li>
            <li><strong>permanent termination of the account, and a lifetime ban from{" "}
              {COMPANY.product}</strong>, where the breach is serious, repeated, or continues after
              a warning or a suspension;</li>
            <li>referral to law enforcement, a regulator, CERT-In or an affected Network, and
              disclosure of the records needed to support it.</li>
          </ul>
          <p>
            We are not required to work through these in order. Uploading or publishing content in
            the child sexual abuse, terrorism, non-consensual intimate imagery or credible-threat
            categories, or committing a Security Incident, will normally result in immediate
            permanent termination and referral, with no warning and no suspension period first.
          </p>
          <p>
            A permanent termination applies to you as a person or organisation, not merely to the
            account. Creating or using another account, Workspace or email address to evade a
            suspension or termination is itself a breach and will result in the same treatment of
            every account involved.
          </p>
          <p className="warn">
            <strong>No refund is given for a suspension or termination under this Section.</strong>{" "}
            Where we suspend or terminate your account for breach, all fees already paid — monthly,
            yearly or lifetime — are forfeited and non-refundable, no credit or pro-rata rebate is
            due for the unused part of any term, and we owe you no compensation for lost access, lost
            scheduling, missed campaign timing, lost reach, lost revenue or any other consequence.
            You remain liable for any amount already due. This is separate from, and unaffected by,
            the ordinary refund position in <a href="#fees">Section 16</a>. Nothing here removes a
            refund right that the law gives you and that cannot lawfully be excluded.
          </p>
          <p>
            Suspension does not delete your data by itself. During a temporary suspension your
            content remains stored and is returned to you when the suspension ends. On permanent
            termination we may delete Your Content after 30 days, except anything we are required or
            entitled to retain under <a href="#security">Section 12</a>, a legal obligation, or an
            investigation.
          </p>
          <p>
            If you believe a measure was taken in error, write to{" "}
            <EmailLink address={COMPANY.grievance.email} /> within 30 days, through the grievance
            route in <a href="#contact">Section 26</a>. We will look at it again and tell you the
            outcome. We are not obliged to disclose our detection methods, our evidence, or the
            identity of a complainant.
          </p>
        </Sec>

        <Sec id="compliance" n={15} title="Platform Policy Compliance">
          <p>
            {COMPANY.product} operates under the Meta Platform Terms, the Meta Developer Policies and
            the YouTube API Services Terms of Service. We are required to enforce those policies
            against our users, and we will act on a Network&rsquo;s instruction to restrict, suspend
            or remove access.
          </p>
          <p>
            If a Network revokes our access, restricts a permission or changes an API, features that
            depend on it may be reduced or withdrawn without notice. We will tell you as soon as we
            reasonably can.
          </p>
          <p>
            Where a Network fines, penalises, suspends or brings a claim against us because of your
            content or conduct, that cost is yours under <a href="#indemnity">Section 23</a>.
          </p>
        </Sec>

        <Sec id="fees" n={16} title="Fees and Payment">
          <p>
            Some features may be offered without charge and others on a paid basis. Where a fee
            applies, the amount, currency, billing frequency and any applicable taxes will be shown
            to you before you are charged, and you will be asked to confirm.
          </p>
          <p>
            Fees already paid are non-refundable except where required by law or expressly stated at
            the point of sale. We may change pricing prospectively on reasonable notice; changes do
            not affect a term you have already paid for.
          </p>
          <p>
            Where an account is suspended or terminated for breach, the no-refund position in{" "}
            <a href="#enforcement">Section 14</a> applies and overrides any refund, trial or
            money-back arrangement otherwise offered.
          </p>
        </Sec>

        <Sec id="availability" n={17} title="Availability and Changes">
          <p>
            We aim to keep the Platform available but do not guarantee uninterrupted or error-free
            operation. Maintenance, upgrades, provider outages, Network outages and defensive
            measures taken during an attack can interrupt service.
          </p>
          <p>
            We may add, modify or discontinue features. Where a change materially reduces
            functionality you rely on, we will give reasonable notice where practicable.
          </p>
        </Sec>

        <Sec id="ip" n={18} title="Intellectual Property">
          <p>
            The {COMPANY.product} name, branding, software, design, documentation and related
            materials remain the property of {COMPANY.legalName} or its licensors. You may not copy,
            modify, misuse, scrape or commercially exploit them without permission.
          </p>
          <p>
            Facebook, Instagram, YouTube and other Network names and logos are the trademarks of
            their respective owners and are used only to identify the destinations you can publish to.
          </p>
        </Sec>

        <Sec id="privacy" n={19} title="Privacy">
          <p>
            Use of {COMPANY.product} is subject to our{" "}
            <Link to="/privacy">Privacy Policy</Link>, which explains what we collect, how
            credentials are protected, who we share information with, and how to have your data
            deleted.
          </p>
        </Sec>

        <Sec id="termination" n={20} title="Ending Your Account">
          <p>
            You may stop using the Platform at any time and request deletion of your account through{" "}
            <Link to="/data-deletion">Delete your data</Link>.
          </p>
          <p>
            We may also end your access for reasons unconnected to breach — non-payment, a legal
            requirement, a Network instruction, or because we are discontinuing the service — and
            where we do, we will give reasonable notice and refund the unused part of any prepaid
            term. Ending an account for breach is governed instead by{" "}
            <a href="#enforcement">Section 14</a>, and no refund is due.
          </p>
          <p>
            Termination does not remove content already published to a Network. That must be deleted
            on the Network itself.
          </p>
          <p>
            Sections 9, 11, 12, 14, 18, 21, 22, 23 and 24 survive termination, along with any other
            provision that by its nature should.
          </p>
        </Sec>

        <Sec id="disclaimer" n={21} title="Disclaimers">
          <p>
            To the maximum extent permitted by law, the Platform is provided &ldquo;as is&rdquo; and
            &ldquo;as available&rdquo;, without warranties of any kind, whether express, implied or
            statutory, including implied warranties of merchantability, fitness for a particular
            purpose and non-infringement.
          </p>
          <p>
            We do not warrant that any post will publish, that a Network will accept it, that reach
            or engagement will result, or that a Network&rsquo;s documented limits will remain
            unchanged.
          </p>
          <p>
            We make no warranty that the Platform is free of Restricted Content uploaded by others,
            that our moderation will identify every breach, or that our security measures will
            withstand every attack. Nothing we say about content standards is a representation that
            content on the Platform has been checked.
          </p>
        </Sec>

        <Sec id="liability" n={22} title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, {COMPANY.product} and {COMPANY.legalName} are not
            liable for indirect, incidental, special, consequential, exemplary or punitive damages, or
            for lost profits, lost revenue, lost data, lost goodwill or missed campaign timing, arising
            from use of the Platform, a failed or delayed publication, Network conduct, token
            expiry, content moderation decisions, or service interruption.
          </p>
          <p>
            We are not liable for Your Content or for any other user&rsquo;s content, for any act or
            omission of another user, for the removal or retention of content under{" "}
            <a href="#moderation">Section 13</a>, for any enforcement measure taken under{" "}
            <a href="#enforcement">Section 14</a>, or for loss caused by a third party&rsquo;s
            unlawful attack on the Platform or on your account.
          </p>
          <p>
            Where liability cannot be excluded, our total aggregate liability for all claims arising
            in any twelve-month period shall not exceed the greater of the amount you paid us for the
            Platform in that period, or INR 5,000.
          </p>
          <p>
            Nothing in these Terms excludes liability that cannot lawfully be excluded, including for
            fraud or wilful misconduct.
          </p>
        </Sec>

        <Sec id="indemnity" n={23} title="Indemnification">
          <p>
            You agree to indemnify, defend and hold harmless {COMPANY.legalName}, its directors,
            employees, agents, service providers and licensors against all claims, losses,
            liabilities, damages, fines, penalties, costs and expenses, including reasonable legal
            and forensic fees, arising from or connected with:
          </p>
          <ul>
            <li>Your Content, including any Restricted Content you upload, store, import, schedule or
              publish;</li>
            <li>your use or misuse of the Platform, or use by anyone using your account or
              Workspace;</li>
            <li>your breach of these Terms, of a Network&rsquo;s terms or policies, or of any law;</li>
            <li>any Security Incident attributable to you, your credentials or your Workspace,
              including the cost of investigating, containing and remediating it and of notifying
              affected people and regulators;</li>
            <li>your infringement of third-party rights, and any claim by a person depicted in,
              named in or harmed by Your Content;</li>
            <li>any action, penalty, fine or loss of access imposed on us by a Network, an app store,
              a payment provider, a regulator or a court because of your content or conduct.</li>
          </ul>
          <p>
            We will tell you about any claim we seek indemnity for and may, at your cost, take
            control of its defence and settlement where our own interests or reputation are engaged.
            This Section survives termination and is not limited by{" "}
            <a href="#liability">Section 22</a>.
          </p>
        </Sec>

        <Sec id="law" n={24} title="Governing Law and Dispute Resolution">
          <p>
            These Terms are governed by the laws of India, without regard to conflict-of-law rules.
            The courts at {COMPANY.jurisdiction} have exclusive jurisdiction over any dispute.
          </p>
          <p>
            Before starting proceedings, please contact us at{" "}
            <EmailLink address={COMPANY.supportEmail} /> so we can try to
            resolve the matter directly. This does not prevent either of us from seeking urgent
            injunctive relief, and does not delay any step we take under{" "}
            <a href="#security">Section 12</a> or <a href="#enforcement">Section 14</a>.
          </p>
        </Sec>

        <Sec id="changes" n={25} title="Changes to Terms">
          <p>
            We may update these Terms from time to time. The revised version will be posted on this
            page with a new Last Updated date. Where changes are material we will notify you by email
            or in the application. Continued use after the change takes effect constitutes acceptance.
          </p>
          <p>
            We will also remind you of these Terms, the <a href="#restricted">restricted content</a>{" "}
            rules and the consequences of breaching them at least once a year, as Rule 3(1)(f) of the
            Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021
            requires.
          </p>
        </Sec>

        <Sec id="contact" n={26} title="Contact and Grievance Officer">
          <p className="contact-block">
            {COMPANY.product}<br />
            {COMPANY.legalName}<br />
            {COMPANY.address.oneLine}<br />
            Email: <EmailLink address={COMPANY.supportEmail} /><br />
            Phone: <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>{COMPANY.phone}</a>
          </p>
          <p>
            <strong>Grievance Officer.</strong> Complaints about content, conduct, a moderation
            decision or an enforcement measure go to the Grievance Officer, appointed under Rule
            3(2)(a) of the Information Technology (Intermediary Guidelines and Digital Media Ethics
            Code) Rules, 2021.
          </p>
          <p className="contact-block">
            {COMPANY.grievance.officerName && <>{COMPANY.grievance.officerName}<br /></>}
            Grievance Officer, {COMPANY.legalName}<br />
            {COMPANY.address.oneLine}<br />
            Email: <EmailLink address={COMPANY.grievance.email} />
          </p>
          <p>
            We acknowledge a complaint within {COMPANY.grievance.acknowledgeHours} hours and dispose
            of it within {COMPANY.grievance.resolveDays} days of receipt. Tell us what happened, when,
            which account or content is involved, and how to reach you.
          </p>
        </Sec>
      </LegalPage>
    </div>
  );
}
